import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const STUDENT_INFO_BASE = `${API_URL}/student-info`;
const ASSESSMENTS_BASE = `${API_URL}/assessments`;

export interface StudentInfo {
    id?: number;
    name: string;
    schoolRollNumber: string;
    controlNumber?: number;
    phoneNumber?: number;
    email?: string;
    address?: string;
    institute_id?: number;
    instituteId?: number;
    assesment_id?: string;
    studentDob?: string;
    schoolSectionId?: number;
    careerNineRollNumber?: string;
}

export interface Assessment {
    id: number;
    assessmentName: string;
    isActive?: boolean;
    starDate?: string;
    endDate?: string;
    questionnaire?: { type?: boolean | null; questionnaireId?: number };
}

export interface BulkAssessmentAssignment {
    userStudentId: number;
    assessmentId: number;
}

export interface AssessmentDetail {
    assessmentId: number;
    assessmentName: string;
    status: string; // 'notstarted' | 'inprogress' | 'completed'
    /** ISO timestamp of when the student finished. Null unless status is 'completed'. */
    completedAt?: string | null;
}

export interface StudentRoleGroupRef {
    id: number;
    name: string;
}

export interface StudentWithMapping {
    id: number;
    name: string;
    schoolRollNumber: string;
    controlNumber?: number;
    phoneNumber?: string;
    email?: string;
    instituteId?: number;
    userStudentId: number;
    assessmentId?: number;
    studentDob?: string;
    username?: string;
    schoolSectionId?: number;
    /** Flat student_info.student_class — Grade fallback when schoolSectionId is unset. */
    studentClass?: string | null;
    gender?: string;
    assessments?: AssessmentDetail[];
    assignedAssessmentIds?: number[];
    roleGroups?: StudentRoleGroupRef[];
}

export interface StudentAnswerDetail {
    questionId: number;
    questionText: string;
    optionId: number;
    optionText: string;
    sectionName?: string | null;
    excelQuestionHeader?: string | null;
    optionNumber?: number;
    isImageOption?: boolean;
}

export function getStudentInfoByInstituteId(instituteId: number) {
    return axios.get<StudentInfo[]>(`${STUDENT_INFO_BASE}/getByInstituteId/${instituteId}`);
}

export function getStudentsWithMappingByInstituteId(instituteId: number) {
    return axios.get<StudentWithMapping[]>(`${STUDENT_INFO_BASE}/getStudentsWithMappingByInstituteId/${instituteId}`);
}

export function getAllStudentInfo() {
    return axios.get<StudentInfo[]>(`${STUDENT_INFO_BASE}/getAll`);
}

// Returns the subset of the given userStudentIds that have at least one successfully
// generated report. Used to gate the "Dashboard" button on the group-student listing.
export function getStudentsWithGeneratedReport(userStudentIds: number[]) {
    return axios.post<number[]>(`${API_URL}/generated-reports/exists/by-students`, { userStudentIds });
}

export function addStudentInfo(student: StudentInfo) {
    return axios.post<StudentInfo>(`${STUDENT_INFO_BASE}/add`, student);
}

export function updateStudentInfo(student: StudentInfo) {
    return axios.post<StudentInfo>(`${STUDENT_INFO_BASE}/update`, student);
}

export function deleteStudentInfo(id: number) {
    return axios.post<void>(`${STUDENT_INFO_BASE}/delete/${id}`);
}

export function updateStudentBasicInfo(data: {
    userStudentId: number;
    name?: string;
    email?: string;
    phoneNumber?: string;
    studentDob?: string;
}) {
    return axios.post(`${STUDENT_INFO_BASE}/updateDemographics`, data);
}

// (sendLoginCredentials is declared below alongside its typed response
// interfaces — single canonical definition.)

// Assessment APIs
// Uses the lightweight cached /get/list-summary projection (id, name, isActive,
// questionnaireType) instead of /getAll, which deep-loaded each AssessmentTable
// + Questionnaire + Sections + Questions + Options + Languages. The response is
// reshaped so existing callers can still read `assessment.questionnaire?.type`.
export function getAllAssessments() {
    return axios
        .get<Array<{ id: number; assessmentName: string; isActive?: boolean; questionnaireType?: boolean | null }>>(
            `${ASSESSMENTS_BASE}/get/list-summary`
        )
        .then((res) => ({
            ...res,
            data: (res.data || []).map<Assessment>((s) => ({
                id: s.id,
                assessmentName: s.assessmentName,
                isActive: s.isActive,
                questionnaire: { type: s.questionnaireType ?? null },
            })),
        }));
}

export function getAssessmentIdNameMap() {
    return axios.get<Record<string, string>>(`${ASSESSMENTS_BASE}/get/list-ids`);
}

export function bulkAlotAssessment(assignments: BulkAssessmentAssignment[]) {
    return axios.post(`${STUDENT_INFO_BASE}/bulkAlotAssessment`, assignments);
}

export function bulkRemoveAssessment(removals: BulkAssessmentAssignment[]) {
    return axios.post(`${STUDENT_INFO_BASE}/bulkRemoveAssessment`, removals);
}

// Result row returned per-student by /send-login-credentials.
// `status === "queued"` means the email was handed off to Odoo (fire-and-forget;
// the actual SMTP send may complete a few seconds later — check Odoo logs for
// the definitive outcome). `status === "failed"` means validation failed
// before dispatch (no email / no username / no DOB on file, etc).
export interface SendCredentialsResult {
    userStudentId: number;
    status: "queued" | "failed";
    email?: string;
    reason?: string;
}

export interface SendCredentialsResponse {
    requested: number;
    // Count of rows successfully queued for dispatch via Odoo.
    sent: number;
    failed: number;
    results: SendCredentialsResult[];
}

// Sends username (from User.username) + password (= student's DOB formatted
// dd-MM-yyyy) by email to every supplied userStudentId. Backend dispatches via
// OdooEmailService asynchronously; rows missing email/username/dob are
// reported as `failed` synchronously rather than aborting the batch.
export function sendLoginCredentials(userStudentIds: number[]) {
    return axios.post<SendCredentialsResponse>(
        `${STUDENT_INFO_BASE}/send-login-credentials`,
        { userStudentIds },
    );
}

// New API to get student answers with question and option details
export function getStudentAnswersWithDetails(userStudentId: number, assessmentId: number) {
    return axios.get<StudentAnswerDetail[]>(`${STUDENT_INFO_BASE}/getStudentAnswersWithDetails`, {
        params: {
            userStudentId,
            assessmentId
        }
    }).catch(error => {
        console.error("API Error:", error.response?.data || error.message);
        throw error;
    });
}

// Bulk fetch answers for multiple students and their assessments
export function getBulkStudentAnswersWithDetails(pairs: { userStudentId: number; assessmentId: number }[]) {
    return axios.post<any[]>(`${STUDENT_INFO_BASE}/getBulkStudentAnswersWithDetails`, pairs);
}

// Bulk fetch demographic data for multiple student+assessment pairs
export function getBulkDemographicData(pairs: { userStudentId: number; assessmentId: number }[]) {
    return axios.post<any[]>(`${API_URL}/student-demographics/bulk-fields`, pairs);
}

// Get demographic fields with values for a single student+assessment
export function getDemographicFieldsForStudent(assessmentId: number, userStudentId: number) {
    return axios.get<any[]>(`${API_URL}/student-demographics/fields/${assessmentId}/${userStudentId}`);
}

// Reset assessment - sets status to 'notstarted' and deletes raw scores
export function resetAssessment(userStudentId: number, assessmentId: number) {
    return axios.post(`${STUDENT_INFO_BASE}/resetAssessment`, {
        userStudentId,
        assessmentId
    });
}

// IRREVERSIBLE: hard-deletes the student and every trace of their data
// (answers, scores, reports, entitlements, counselling, demographics, logs,
// account rows; payment transactions are anonymized, not deleted). Returns
// per-table deletion counts.
export function purgeStudent(userStudentId: number) {
    return axios.delete<{ success: boolean; deleted: Record<string, number>; loginRow: string }>(
        `${STUDENT_INFO_BASE}/purge-student/${userStudentId}`
    );
}

// Bulk fetch proctoring data for multiple student+assessment pairs
export function getBulkProctoringData(pairs: { userStudentId: number; assessmentId: number }[]) {
    return axios.post<any[]>(`${API_URL}/assessment-proctoring/getBulkProctoringData`, pairs);
}

// Export proctoring data as Excel from backend (server-side generation)
export function exportProctoringExcel(pairs: { userStudentId: number; assessmentId: number }[]) {
    return axios.post(`${API_URL}/assessment-proctoring/export-excel`, pairs, {
        responseType: 'blob',
        headers: {
            'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/json',
            'Content-Type': 'application/json',
        },
    });
}

// Export the combined legacy workbook (Raw Data + Master Sheet) for one assessment
export function exportCombinedAssessmentExcel(assessmentId: number, userStudentIds: number[]) {
    return axios.post(`${API_URL}/general-assessment/export-combined-excel`, {
        assessmentId,
        userStudentIds,
    }, {
        responseType: 'blob',
        headers: {
            'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/json',
            'Content-Type': 'application/json',
        },
    });
}

// Export the Navigator360 school dashboard (9 sheets, already filled in).
// userStudentIds carries whatever the caller's filters and selection left in
// view; an empty array means the whole assessment. classFilter is sheet 2's own
// filter — "All", or a class number.
export function exportSchoolDashboardExcel(
    assessmentId: number,
    userStudentIds: number[],
    classFilter: string = "All",
) {
    return axios.post(`${API_URL}/general-assessment/export-school-dashboard`, {
        assessmentId,
        userStudentIds,
        classFilter,
    }, {
        responseType: 'blob',
        headers: {
            'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/json',
            'Content-Type': 'application/json',
        },
    });
}

// Game Results APIs (Firestore via backend)
export function getAllGameResults() {
    return axios.get<any[]>(`${API_URL}/game-results/getAll`);
}

// Student Scores APIs
export interface StudentScoreDetail {
    measuredQualityTypeName: string;
    measuredQualityTypeDisplayName: string;
    measuredQualityName?: string;
    rawScore: number;
}

export interface StudentScoresResponse {
    student: {
        name: string;
        rollNumber: string;
        studentClass: string | null;
        dob: string;
    };
    scores: StudentScoreDetail[];
    status: string;
}

// Get individual student scores
export function getStudentScores(userStudentId: number, assessmentId: number) {
    return axios.get<StudentScoresResponse>(`${STUDENT_INFO_BASE}/getStudentScores`, {
        params: { userStudentId, assessmentId }
    });
}

// Bulk export scores by institute - returns Excel blob
export function exportScoresByInstitute(instituteId: number, assessmentId: number) {
    return axios.get(`${STUDENT_INFO_BASE}/exportScoresByInstitute/${instituteId}`, {
        params: { assessmentId },
        responseType: 'blob'
    });
}


export function getContactPersonsByInstitute(instituteCode: number) {
    return axios.get(`${API_URL}/contact-person/by-institute/${instituteCode}`);
}

export function assignStudentsToContactPerson(payload: {
    contactPersonId: number;
    userStudentIds: number[];
    instituteId: number;
}) {
    return axios.post(`${API_URL}/contact-person/assign-students`, payload);
}

// BET Report APIs
export interface BetReportColumn {
    key: string;
    header: string;
    questionId: number;
    optionId?: number;
    isMQT: boolean;
}

export interface BetReportResponse {
    columns: BetReportColumn[];
    rows: Record<string, any>[];
}

export function getBetReport(instituteId: number, assessmentId: number) {
    return axios.get<BetReportResponse>(
        `${STUDENT_INFO_BASE}/bet-report/${instituteId}/${assessmentId}`
    );
}

// ── One-click report generation (generates data + HTML + returns URL) ──

export interface OneClickReportResponse {
    reportUrl: string;
    studentName: string;
    status: string;
}

export function generateBetReportOneClick(assessmentId: number, userStudentId: number, force = false) {
    return axios.post<OneClickReportResponse>(`${API_URL}/bet-report-data/one-click-report`, {
        assessmentId,
        userStudentId,
        force,
    }, { timeout: 120000 }); // 2 min timeout for report generation
}

export function generateNavigatorReportOneClick(assessmentId: number, userStudentId: number, force = false) {
    return axios.post<OneClickReportResponse>(`${API_URL}/navigator-report-data/one-click-report`, {
        assessmentId,
        userStudentId,
        force,
    }, { timeout: 120000 }); // 2 min timeout for report generation + AI
}

/**
 * One-click pager (Navigator 4-pager) report generator for the Student
 * Management page. Response payload uses different keys than the legacy BET /
 * Navigator endpoints because the pager pipeline tells the caller whether the
 * row already existed (for Generate vs Regenerate label semantics).
 */
export interface PagerOneClickReportResponse {
    status: string;          // "ready" | "not_ready" | "failed"
    reportType: "pager";
    reportUrl: string;
    alreadyExisted: boolean;
    generatedAt?: string;
}

export function generatePagerReportOneClick(assessmentId: number, userStudentId: number, force = false) {
    return axios.post<PagerOneClickReportResponse>(`${API_URL}/pager-report-data/one-click-report`, {
        assessmentId,
        userStudentId,
        force,
    }, { timeout: 120000 });
}

/**
 * Unified report-generation endpoint (Phase 1-b-2 rework). Backend resolves
 * report type + subtype from the Questionnaire's reportType / reportSubtype
 * FKs, computes / reuses placeholder data, re-renders the HTML template from
 * Spaces, and returns the public CDN URL. Replaces the per-type BET /
 * Navigator / Pager one-click endpoints; the old endpoints remain as thin
 * forwarders during the deprecation window.
 */
export interface UnifiedReportResponse {
    status: string;          // "generated" | "not_ready" | "failed"
    reportType: string;      // "bet" | "legacy" | "pager"
    reportSubtype: string;   // "default" | "insight" | "subject" | "career"
    reportUrl: string;
    calculatedAt?: string;
    renderedAt?: string;
    alreadyExisted: boolean;
    error?: string;
    errorCode?: string;
}

export function generateUnifiedReportOneClick(
    assessmentId: number, userStudentId: number, force = false
) {
    return axios.post<UnifiedReportResponse>(`${API_URL}/generate-report-unified`, {
        assessmentId,
        userStudentId,
        force,
    }, { timeout: 120000 });
}

/**
 * Bulk fetch every {@code generated_report} row for one student. Powers the
 * Student Management view-modal hydration so each assessment's Generate /
 * Download / Regenerate button starts with the right state instead of
 * forgetting prior generations on every modal open. Backend endpoint at
 * {@code GET /generated-reports/by-student/:userStudentId}.
 */
export interface GeneratedReportRow {
    assessmentId: number;
    typeOfReport: string;   // "bet" | "navigator" | "pager"
    reportStatus: string;   // "notGenerated" | "generated" | "failed"
    reportUrl: string | null;
    visibleToStudent?: boolean;
    updatedAt?: string;
}

export function getGeneratedReportsForStudent(userStudentId: number) {
    return axios.get<GeneratedReportRow[]>(`${API_URL}/generated-reports/by-student/${userStudentId}`);
}

// ── Student role-group + effective-permission editor (Data Download page) ──
export interface StudentRoleGroupDetail {
    mappingId: number;
    id: number;
    name: string;
    permissionCodes: string[];
}

export interface StudentRoleGroupPayload {
    userStudentId: number;
    userId: number;
    roleGroups: StudentRoleGroupDetail[];
    effectivePermissions: string[];
}

export function getStudentRoleGroups(userStudentId: number) {
    return axios.get<StudentRoleGroupPayload>(
        `${STUDENT_INFO_BASE}/${userStudentId}/role-groups`
    );
}

export function updateStudentRoleGroups(userStudentId: number, roleGroupIds: number[]) {
    return axios.put<StudentRoleGroupPayload>(
        `${STUDENT_INFO_BASE}/${userStudentId}/role-groups`,
        { roleGroupIds }
    );
}

export function getRoleGroupCatalog() {
    return axios.get<StudentRoleGroupRef[]>(
        `${STUDENT_INFO_BASE}/role-groups/catalog`
    );
}
